import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  SignOut,
  CaretLeft,
  Users,
  ClockCounterClockwise,
  ShieldCheck,
  Trash,
  SignIn,
  Star,
  ArrowClockwise,
  MagnifyingGlass,
  User,
  Buildings,
  ArrowLeft,
} from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import ThemeToggle from "../components/ThemeToggle";

/**
 * Admin panel — restricted to users with is_admin=true.
 * Two tabs: Usuarios (list + stats + admin toggle + delete) and Historial (login/logout log).
 */
export default function Admin() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("users");
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (!loading) {
      if (!user) navigate("/login", { replace: true });
      else if (!user.is_admin) navigate("/finanzas", { replace: true });
    }
  }, [loading, user, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center login-bg">
        <div className="text-white/70 font-mono text-sm tracking-widest">
          LOADING<span className="animate-pulse">...</span>
        </div>
      </div>
    );
  }

  if (!user.is_admin) {
    return null;
  }

  return (
    <div className="relative flex min-h-screen bg-deck-bg text-white">
      <div className="noise-overlay" />

      {/* Sidebar */}
      <aside
        data-testid="admin-sidebar"
        className={`${collapsed ? "w-[72px]" : "w-[260px]"} shrink-0 bg-deck-sidebar border-r border-white/5 flex flex-col transition-all duration-300 z-10 relative`}
      >
        <div className="px-5 py-6 flex items-center gap-3 border-b border-white/5">
          <div className="w-10 h-10 rounded-2xl bg-red-500 flex items-center justify-center deck-tile shrink-0">
            <ShieldCheck size={22} weight="fill" className="text-white" />
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-red-400">ADMIN CONSOLE</div>
              <div className="text-base font-heading font-bold text-white truncate">Panel</div>
            </div>
          )}
        </div>

        <button
          data-testid="admin-back-btn"
          onClick={() => navigate("/finanzas")}
          className="mx-3 mt-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/12 text-white/70 hover:text-white text-xs font-mono uppercase tracking-widest transition"
        >
          <ArrowLeft size={12} weight="bold" />
          {!collapsed && <span>Volver a Finanzas</span>}
        </button>

        <nav className="flex-1 overflow-y-auto px-3 py-4 flex flex-col gap-1">
          {!collapsed && (
            <span className="px-2 pb-2 text-[10px] font-mono uppercase tracking-[0.18em] text-white/40">
              Módulos
            </span>
          )}
          {[
            { key: "users", label: "Usuarios", Icon: Users },
            { key: "history", label: "Historial", Icon: ClockCounterClockwise },
          ].map((t) => {
            const Icon = t.Icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                data-testid={`admin-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                title={t.label}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition ${
                  active
                    ? "bg-white/8 border border-white/10"
                    : "hover:bg-white/5 border border-transparent"
                }`}
              >
                <div
                  className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 deck-tile ${active ? "bg-red-500" : "bg-white/6"}`}
                >
                  <Icon size={16} weight="fill" className="text-white" />
                </div>
                {!collapsed && (
                  <span className={`flex-1 truncate text-sm font-heading font-semibold ${active ? "text-white" : "text-white/60"}`}>
                    {t.label}
                  </span>
                )}
                {!collapsed && active && <span className="w-1.5 h-1.5 rounded-full bg-red-500" />}
              </button>
            );
          })}
        </nav>

        <div className="border-t border-white/5 p-3">
          <div className="flex items-center gap-3 p-2">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full border border-white/10" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center">
                <ShieldCheck size={16} className="text-white/60" />
              </div>
            )}
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <div className="text-xs font-heading font-semibold truncate">{user?.name}</div>
                <div className="text-[10px] font-mono text-red-400 truncate">ADMIN · {user?.email}</div>
              </div>
            )}
            {!collapsed && (
              <button
                data-testid="admin-logout-btn"
                onClick={handleLogout}
                title="Cerrar sesión"
                className="w-8 h-8 rounded-lg hover:bg-white/10 flex items-center justify-center transition"
              >
                <SignOut size={14} weight="bold" className="text-white/60" />
              </button>
            )}
          </div>
          <button
            data-testid="admin-sidebar-toggle"
            onClick={() => setCollapsed((v) => !v)}
            className="mt-2 w-full flex items-center justify-center gap-2 py-2 rounded-lg hover:bg-white/5 text-white/40 hover:text-white/70 text-[10px] font-mono uppercase tracking-[0.18em] transition"
          >
            <CaretLeft size={12} weight="bold" className={`transition-transform ${collapsed ? "rotate-180" : ""}`} />
            {!collapsed && "Contraer"}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 relative z-[1]">
        <header
          data-testid="admin-topbar"
          className="px-6 md:px-10 py-5 flex items-center gap-3 border-b border-white/5 bg-deck-bg/60 backdrop-blur-md sticky top-0 z-20"
        >
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-red-400">
              ADMIN · {tab === "users" ? "USUARIOS" : "HISTORIAL"}
            </div>
            <h1 className="text-2xl md:text-3xl font-heading font-black text-white tracking-tight truncate">
              {tab === "users" ? "Gestión de usuarios" : "Historial de acceso"}
            </h1>
          </div>
          <div className="flex-1" />
          <ThemeToggle />
        </header>

        <main className="flex-1 px-6 md:px-10 py-6 md:py-10 overflow-auto">
          {tab === "users" ? <UsersTab currentUserId={user.user_id} /> : <HistoryTab />}
        </main>
      </div>
    </div>
  );
}

// ---------- Users Tab ----------
function UsersTab({ currentUserId }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data || []);
    } catch (err) {
      toast.error("No se pudieron cargar los usuarios");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleAdmin = async (u) => {
    if (u.user_id === currentUserId) {
      toast.error("No puedes cambiar tu propio rol");
      return;
    }
    setBusy(u.user_id);
    try {
      const { data } = await api.post(`/admin/users/${u.user_id}/toggle-admin`);
      setUsers((prev) => prev.map((x) => (x.user_id === u.user_id ? { ...x, is_admin: data.is_admin } : x)));
      toast.success(data.is_admin ? `${u.email} es admin` : `${u.email} ya no es admin`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setBusy(null);
    }
  };

  const deleteUser = async (u) => {
    if (u.user_id === currentUserId) {
      toast.error("No puedes borrarte a ti mismo");
      return;
    }
    const ok = window.confirm(
      `⚠️ Borrar a ${u.email}?\n\nEsto elimina PERMANENTEMENTE:\n· ${u.counts.accounts} cuentas\n· ${u.counts.debts} deudas\n· ${u.counts.transactions} transacciones\n· ${u.counts.documents} documentos\n· ${u.counts.decks} decks (LaunchPad)\n\nEsta acción NO se puede deshacer.`,
    );
    if (!ok) return;
    setBusy(u.user_id);
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      setUsers((prev) => prev.filter((x) => x.user_id !== u.user_id));
      toast.success(`Usuario ${u.email} eliminado`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al borrar");
    } finally {
      setBusy(null);
    }
  };

  const filtered = q.trim()
    ? users.filter((u) =>
        (u.email || "").toLowerCase().includes(q.toLowerCase())
        || (u.name || "").toLowerCase().includes(q.toLowerCase()))
    : users;

  return (
    <div className="space-y-4" data-testid="admin-users-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[240px] flex items-center gap-2 rounded-lg bg-black/40 border border-white/10 focus-within:border-red-500/50 px-3 py-2">
          <MagnifyingGlass size={14} className="text-white/40" weight="bold" />
          <input
            data-testid="admin-users-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nombre o email..."
            className="flex-1 bg-transparent outline-none text-sm text-white font-mono placeholder-white/30"
          />
        </div>
        <div className="text-[11px] font-mono text-white/50">
          {filtered.length} de {users.length} usuarios
        </div>
        <button
          data-testid="admin-users-reload"
          onClick={load}
          disabled={loading}
          className="w-9 h-9 rounded-lg bg-white/5 hover:bg-white/10 border border-white/8 text-white/60 flex items-center justify-center"
        >
          <ArrowClockwise size={12} weight="bold" className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      <div className="rounded-lg border border-white/8 overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-white/40 bg-black/40 border-b border-white/6">
          <div className="col-span-4">Usuario</div>
          <div className="col-span-2">Último login</div>
          <div className="col-span-4">Datos</div>
          <div className="col-span-2 text-right">Acciones</div>
        </div>
        <div className="divide-y divide-white/6">
          {loading ? (
            <div className="p-8 text-center text-xs font-mono text-white/40">
              <ArrowClockwise size={18} weight="bold" className="animate-spin mx-auto mb-2" />
              Cargando usuarios...
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-xs font-mono text-white/40">Sin usuarios</div>
          ) : (
            filtered.map((u) => {
              const isMe = u.user_id === currentUserId;
              return (
                <div
                  key={u.user_id}
                  data-testid={`admin-user-row-${u.user_id}`}
                  className="grid grid-cols-12 gap-2 px-3 py-3 items-center hover:bg-white/4"
                >
                  <div className="col-span-4 flex items-center gap-2 min-w-0">
                    {u.picture ? (
                      <img src={u.picture} alt="" className="w-8 h-8 rounded-full border border-white/10" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                        <User size={12} weight="bold" className="text-white/60" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <div className="text-sm font-heading font-bold text-white truncate">{u.name || "(sin nombre)"}</div>
                        {u.is_admin && (
                          <span
                            className="inline-flex items-center gap-0.5 text-[9px] font-mono uppercase tracking-widest px-1 py-0.5 rounded bg-red-500/25 text-red-300 font-bold"
                            title="Administrador"
                          >
                            <Star size={8} weight="fill" /> ADMIN
                          </span>
                        )}
                        {isMe && (
                          <span className="text-[9px] font-mono uppercase tracking-widest px-1 py-0.5 rounded bg-blue-500/25 text-blue-300 font-bold">
                            YO
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] font-mono text-white/50 truncate">{u.email}</div>
                    </div>
                  </div>
                  <div className="col-span-2 text-[11px] font-mono text-white/60">
                    {u.last_login_at ? formatRelative(u.last_login_at) : <span className="text-white/30">nunca</span>}
                  </div>
                  <div className="col-span-4 text-[11px] font-mono text-white/60 flex flex-wrap gap-x-3 gap-y-0.5">
                    <span>{u.counts.accounts} cta</span>
                    <span>{u.counts.debts} deud</span>
                    <span>{u.counts.transactions} tx</span>
                    <span>{u.counts.documents} docs</span>
                    <span>{u.counts.decks} decks</span>
                  </div>
                  <div className="col-span-2 flex items-center justify-end gap-1">
                    <button
                      data-testid={`admin-user-view-${u.user_id}`}
                      onClick={() => setDetail(u)}
                      className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/12 text-white/60 hover:text-white flex items-center justify-center transition"
                      title="Ver detalle"
                    >
                      <MagnifyingGlass size={12} weight="bold" />
                    </button>
                    <button
                      data-testid={`admin-toggle-admin-${u.user_id}`}
                      onClick={() => toggleAdmin(u)}
                      disabled={busy === u.user_id || isMe}
                      className={`w-8 h-8 rounded-lg flex items-center justify-center transition disabled:opacity-40 ${
                        u.is_admin
                          ? "bg-red-500/25 hover:bg-red-500/40 text-red-300"
                          : "bg-white/5 hover:bg-white/12 text-white/60 hover:text-white"
                      }`}
                      title={u.is_admin ? "Quitar admin" : "Hacer admin"}
                    >
                      <ShieldCheck size={12} weight={u.is_admin ? "fill" : "bold"} />
                    </button>
                    <button
                      data-testid={`admin-user-delete-${u.user_id}`}
                      onClick={() => deleteUser(u)}
                      disabled={busy === u.user_id || isMe}
                      className="w-8 h-8 rounded-lg bg-white/5 hover:bg-red-500/25 text-white/60 hover:text-red-300 flex items-center justify-center transition disabled:opacity-40"
                      title="Borrar usuario"
                    >
                      <Trash size={12} weight="bold" />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {detail && <UserDetailDialog userId={detail.user_id} onClose={() => setDetail(null)} />}
    </div>
  );
}

function UserDetailDialog({ userId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: res } = await api.get(`/admin/users/${userId}`);
        if (!cancelled) setData(res);
      } catch (err) {
        if (!cancelled) toast.error("No se pudo cargar el detalle");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  return (
    <div
      data-testid="admin-user-detail"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl max-h-[85vh] overflow-hidden rounded-2xl border border-white/10 bg-[#0F1017] shadow-2xl flex flex-col"
      >
        <div className="p-5 border-b border-white/8 flex items-start justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-red-400">
              ADMIN · DETALLE
            </div>
            <div className="text-lg font-heading font-bold text-white mt-0.5">
              {data?.user?.name || "..."}
            </div>
            <div className="text-[11px] font-mono text-white/50 mt-1">
              {data?.user?.email}
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-white/10 text-white/50 hover:text-white flex items-center justify-center">
            ✕
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {loading || !data ? (
            <div className="py-8 text-center text-xs font-mono text-white/40">
              <ArrowClockwise size={16} weight="bold" className="animate-spin mx-auto" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                {Object.entries(data.counts).map(([k, v]) => (
                  <div key={k} className="p-2 rounded-lg bg-black/30 border border-white/6">
                    <div className="text-[9px] font-mono uppercase tracking-widest text-white/40">{k}</div>
                    <div className="text-lg font-mono font-bold text-white tabular-nums">{v}</div>
                  </div>
                ))}
              </div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-2">
                Últimos {data.events.length} eventos de login
              </div>
              <div className="space-y-1">
                {data.events.length === 0 ? (
                  <div className="py-6 text-center text-xs font-mono text-white/30">
                    Sin eventos registrados aún.
                  </div>
                ) : (
                  data.events.map((e) => (
                    <div key={e.event_id} className="flex items-center gap-2 p-2 rounded bg-black/30 text-[11px] font-mono">
                      <span
                        className={`w-2 h-2 rounded-full ${e.kind === "login" ? "bg-emerald-400" : "bg-red-400"}`}
                        title={e.kind}
                      />
                      <span className={e.kind === "login" ? "text-emerald-300" : "text-red-300"}>
                        {e.kind.toUpperCase()}
                      </span>
                      <span className="text-white/40">{e.created_at?.slice(0, 19).replace("T", " ")}</span>
                      <span className="text-white/40 truncate">{e.ip || "-"}</span>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- History Tab ----------
function HistoryTab() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", "200");
      if (kind !== "all") params.set("kind", kind);
      const { data } = await api.get(`/admin/login-events?${params.toString()}`);
      setEvents(data.items || []);
    } catch (err) {
      toast.error("No se pudo cargar el historial");
    } finally {
      setLoading(false);
    }
  }, [kind]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="admin-history-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="inline-flex items-center gap-1 p-1 rounded-lg bg-black/40 border border-white/8">
          {[
            { key: "all", label: "Todo" },
            { key: "login", label: "Login" },
            { key: "logout", label: "Logout" },
          ].map((o) => {
            const active = kind === o.key;
            return (
              <button
                key={o.key}
                data-testid={`admin-history-${o.key}`}
                onClick={() => setKind(o.key)}
                className={`px-3 py-1 text-[10px] font-mono uppercase tracking-widest font-bold rounded transition ${
                  active ? "bg-white/12 text-white" : "text-white/45 hover:text-white/80"
                }`}
              >
                {o.label}
              </button>
            );
          })}
        </div>
        <div className="text-[11px] font-mono text-white/50">
          {events.length} eventos
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="w-9 h-9 rounded-lg bg-white/5 hover:bg-white/10 border border-white/8 text-white/60 flex items-center justify-center"
        >
          <ArrowClockwise size={12} weight="bold" className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      <div className="rounded-lg border border-white/8 overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-white/40 bg-black/40 border-b border-white/6">
          <div className="col-span-2">Cuándo</div>
          <div className="col-span-2">Tipo</div>
          <div className="col-span-4">Usuario</div>
          <div className="col-span-2">IP</div>
          <div className="col-span-2">Agente</div>
        </div>
        <div className="divide-y divide-white/6 max-h-[70vh] overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center text-xs font-mono text-white/40">
              <ArrowClockwise size={18} weight="bold" className="animate-spin mx-auto mb-2" />
              Cargando historial...
            </div>
          ) : events.length === 0 ? (
            <div className="p-8 text-center text-xs font-mono text-white/40">
              Sin eventos aún.
            </div>
          ) : (
            events.map((e) => (
              <div
                key={e.event_id}
                data-testid={`admin-event-${e.event_id}`}
                className="grid grid-cols-12 gap-2 px-3 py-2 items-center text-[11px] font-mono hover:bg-white/4"
              >
                <div className="col-span-2 text-white/60">
                  {e.created_at?.slice(0, 19).replace("T", " ")}
                </div>
                <div className="col-span-2">
                  <span
                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded uppercase tracking-widest text-[9px] font-bold ${
                      e.kind === "login" ? "bg-emerald-500/20 text-emerald-300" : "bg-red-500/20 text-red-300"
                    }`}
                  >
                    {e.kind === "login" ? <SignIn size={9} weight="bold" /> : <SignOut size={9} weight="bold" />}
                    {e.kind}
                  </span>
                </div>
                <div className="col-span-4 text-white truncate">{e.email}</div>
                <div className="col-span-2 text-white/60 truncate">{e.ip || "-"}</div>
                <div className="col-span-2 text-white/40 truncate" title={e.user_agent || ""}>
                  {(e.user_agent || "-").slice(0, 24)}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function formatRelative(isoString) {
  if (!isoString) return "";
  try {
    const then = new Date(isoString);
    const now = new Date();
    const diff = Math.max(0, (now - then) / 1000);
    if (diff < 60) return "ahora";
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    if (diff < 2592000) return `${Math.floor(diff / 86400)}d`;
    return then.toISOString().slice(0, 10);
  } catch {
    return "";
  }
}
